import { motion } from "framer-motion";
import { useInView } from "framer-motion";
import { useRef } from "react";
import { Instagram, MessageCircle, AlertTriangle, TrendingDown, XCircle } from "lucide-react";

const ProblemSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const problems = [
    { icon: TrendingDown, text: "Difícil medir resultados de anúncios" },
    { icon: XCircle, text: "Leads se perdem nas mensagens" },
    { icon: AlertTriangle, text: "Baixa conversão do tráfego pago" },
  ];

  return (
    <section id="problema" className="py-24 md:py-32 relative" ref={ref}>
      <div className="absolute inset-0" style={{ background: "var(--gradient-glow)" }} />
      <div className="container mx-auto px-6 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <span className="text-sm font-mono text-secondary uppercase tracking-widest">O problema</span>
          <h2 className="text-3xl md:text-5xl font-bold mt-4 text-balance">
            A maioria das empresas tenta vender{" "}
            <span className="gradient-text-blue">apenas pelo Instagram.</span>
          </h2>
        </motion.div>

        {/* Flow visualization */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.3, duration: 0.6 }}
          className="flex flex-col md:flex-row items-center justify-center gap-4 md:gap-6 mb-16"
        >
          {[
            { icon: Instagram, label: "Instagram", sub: "Publica conteúdo" },
            { icon: MessageCircle, label: "WhatsApp", sub: "Responde clientes" },
            { icon: AlertTriangle, label: "Leads perdidos", sub: "Sem organização" },
          ].map((item, i) => (
            <div key={i} className="flex items-center gap-4 md:gap-6">
              <div className="glass-card rounded-2xl p-6 text-center w-48">
                <item.icon className={`w-8 h-8 mx-auto mb-3 ${i === 2 ? "text-destructive" : "text-primary"}`} />
                <p className="font-semibold text-sm">{item.label}</p>
                <p className="text-xs text-muted-foreground mt-1">{item.sub}</p>
              </div>
              {i < 2 && <span className="text-2xl text-muted-foreground hidden md:block">→</span>}
            </div>
          ))}
        </motion.div>

        <div className="grid md:grid-cols-3 gap-6 max-w-3xl mx-auto">
          {problems.map((p, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 0.5 + i * 0.1 }}
              className="glass-card rounded-xl p-5 flex items-start gap-3"
            >
              <p.icon className="w-5 h-5 text-destructive shrink-0 mt-0.5" />
              <p className="text-sm text-muted-foreground">{p.text}</p>
            </motion.div>
          ))}
        </div>

        <motion.p
          initial={{ opacity: 0 }}
          animate={isInView ? { opacity: 1 } : {}}
          transition={{ delay: 0.8 }}
          className="text-center text-muted-foreground text-sm mt-12 max-w-xl mx-auto"
        >
          Mais de 70% das pequenas empresas usam redes sociais para vender, mas menos da metade possui um site próprio. Empresas pagam por anúncios, mas enviam visitantes para Instagram ou WhatsApp sem uma página preparada para converter.
        </motion.p>
      </div>
    </section>
  );
};

export default ProblemSection;
